import { State, MainThunkDispatch } from "../../../Reducers"
import { failMessage, successMessage } from "../MessagesActions"
import { InterfaceType } from "../../Types"
import { uploadData } from "../../API/Data/DataActions"
import {
    isQueued,
    isInProgress,
    UploadQueueItemFailed,
    UploadQueueItemQueued,
    UploadQueueItem
} from "../../../Models/Client/UploadQueueItem"
import { asError } from "../../../Helpers/Functions/error"
import { appendTimestampToFileName } from "../../../Helpers/Functions/random"

/**
 * Upload files in given queue one-by-one until there is no item of queued status in it.
 */
const processQueue =
    (stateKey: string, appendTimestamp: boolean) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        let maybeError: Error | undefined

        const itemsToProcess = (
            getState().client.interface.uploader[stateKey] || []
        ).filter(isQueued)

        if (itemsToProcess.length === 0) {
            return
        }

        for (const item of itemsToProcess) {
            try {
                await dispatch(
                    uploadQueuedItem(stateKey, item, appendTimestamp)
                )
            } catch (e) {
                maybeError = asError(e) // remember the error and continue processing
            }
        }

        // Process items which are enqueued after starting to process
        await dispatch(processQueue(stateKey, appendTimestamp))

        if (maybeError) {
            throw maybeError
        }
    }

export const upload =
    (
        stateKey: string,
        files: File[],
        squashSuccessMessage: boolean,
        appendTimestamp: boolean
    ) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const items = getState().client.interface.uploader[stateKey] || []

        dispatch({
            type: InterfaceType.uploader_enqueueUploads,
            payload: {
                stateKey,
                files
            }
        })

        if (items.filter(isInProgress).length > 0) {
            return
        }

        try {
            await dispatch(processQueue(stateKey, appendTimestamp))

            if (!squashSuccessMessage) {
                dispatch(successMessage("Uploaded successfully"))
            }
        } catch (e) {
            dispatch(failMessage("Could not upload all data", e))
        }
    }

export const uploadQueuedItem =
    (
        stateKey: string,
        item: UploadQueueItemFailed | UploadQueueItemQueued,
        appendTimestamp: boolean
    ) =>
    async (dispatch: MainThunkDispatch) => {
        dispatch({
            type: InterfaceType.uploader_startUpload,
            payload: {
                stateKey,
                name: item.name
            }
        })

        try {
            const blob = await fetch(item.objectUrl).then(res => res.blob())
            const objectName = appendTimestamp
                ? appendTimestampToFileName(item.name)
                : item.name
            const url = await dispatch(
                uploadData(blob, objectName, {
                    onProgress: progress => {
                        dispatch({
                            type: InterfaceType.uploader_progress,
                            payload: { stateKey, name: item.name, progress }
                        })
                    }
                })
            )

            dispatch({
                type: InterfaceType.uploader_finishUploads,
                payload: {
                    stateKey,
                    name: item.name,
                    url
                }
            })
        } catch (e) {
            dispatch({
                type: InterfaceType.uploader_failUploads,
                payload: {
                    stateKey,
                    name: item.name,
                    reason: asError(e).message
                }
            })
            throw e
        }
    }

export const removeQueueItem =
    (stateKey: string, item: UploadQueueItem) =>
    (dispatch: MainThunkDispatch) =>
        dispatch({
            type: InterfaceType.uploader_removeQueueItem,
            payload: {
                stateKey,
                name: item.name
            }
        })
